import { useState } from "react";

import AiStartIcon from "atoms/icon/icons/ai-start-icon.svg";
import Collapse from "atoms/icon/icons/collapse.svg";
import Refresh from "atoms/icon/icons/refresh.svg";
import { IconButton } from "atoms/icon-button/IconButton";
import { Modal } from "atoms/modal/Modal";
import { useFeatureFlag } from "services/Flags";

import { replacePersonaPlaceholders } from "../adonis.helpers";
import { AdonisIdentity, AdonisSession } from "../adonis.types";
import AdonisChatComponent from "../AdonisChatComponent/AdonisChatComponent";
import { useAdonisChat } from "../AdonisChatProvider/AdonisChatProvider";

type AdonisChatHeaderProps = {
  readonly persona: AdonisIdentity;
  readonly onClose: () => void;
  readonly onRefresh: () => void;
};

function AdonisChatHeader({
  persona,
  onClose,
  onRefresh,
}: AdonisChatHeaderProps) {
  return (
    <div className="flex flex-col bg-white bg-opacity-70">
      <div className="relative flex items-center justify-center px-7 pt-4">
        <div className="flex items-center gap-2">
          <AiStartIcon className="h-4 w-4" />
          <span className="font-sans text-body2 font-bold uppercase tracking-[0.573px]">
            {persona.name.toUpperCase()} AI
          </span>
        </div>
        <div className="absolute right-7 flex items-center gap-4">
          <IconButton
            svg={Refresh}
            size="xsmall"
            style="iconOnly"
            onClick={onRefresh}
          />
          <IconButton
            svg={Collapse}
            size="xsmall"
            style="iconOnly"
            onClick={onClose}
          />
        </div>
      </div>
    </div>
  );
}

type AdonisChatConfirmationModalProps = {
  readonly open: boolean;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
  readonly personaName: string;
};

function AdonisChatConfirmationModal({
  open,
  onCancel,
  onConfirm,
  personaName,
}: AdonisChatConfirmationModalProps) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-[320px] rounded-lg bg-white p-6 shadow-lg">
        <div className="text-center">
          <h2 className="mb-4 text-body1 ">
            You&apos;re about to start a new conversation with {personaName}.
            Are you sure you want to restart?
          </h2>
          <div className="flex gap-3">
            <button
              onClick={onCancel}
              className="flex-1 rounded-md border border-gray-300 px-4 py-2 text-gray-600 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="flex-1 rounded-md bg-[#B39B84] px-4 py-2 text-white hover:bg-[#9F8975]"
            >
              Yes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

type AdonisChatProps = {
  readonly onClose: () => void;
  readonly session: AdonisSession;
  readonly resetSession: () => void;
  readonly isOpen: boolean;
};

export default function AdonisChat({
  onClose,
  isOpen,
  session,
  resetSession,
}: AdonisChatProps) {
  const [open, setOpen] = useState(false);

  const adonisChatText = useFeatureFlag("adonisChatText");
  const { persona, pendingMessage } = useAdonisChat();

  const defaultText = {
    header: `I'm {name}, your AI treatment advisor.`,
    new_chat: "NEW CHAT STARTED",
    chose_question_or_chat: "CHOOSE A QUESTION OR ASK BELOW TO START",
    chat_box: "Type a message...",
  };

  const chatText = adonisChatText
    ? {
        header: replacePersonaPlaceholders(
          adonisChatText.header || defaultText.header,
          persona
        ),
        new_chat: replacePersonaPlaceholders(
          adonisChatText.new_chat || defaultText.new_chat,
          persona
        ),
        chose_question_or_chat: replacePersonaPlaceholders(
          adonisChatText.chose_question_or_chat ||
            defaultText.chose_question_or_chat,
          persona
        ),
        chat_box: replacePersonaPlaceholders(
          adonisChatText.chat_box || defaultText.chat_box,
          persona
        ),
      }
    : {
        header: replacePersonaPlaceholders(defaultText.header, persona),
        new_chat: replacePersonaPlaceholders(defaultText.new_chat, persona),
        chose_question_or_chat: replacePersonaPlaceholders(
          defaultText.chose_question_or_chat,
          persona
        ),
        chat_box: replacePersonaPlaceholders(defaultText.chat_box, persona),
      };

  const handleResetSession = () => {
    resetSession();
    setOpen(false);
  };

  const handleOpenConfirmationModal = () => {
    setOpen(true);
  };

  const handleCloseConfirmationModal = () => {
    setOpen(false);
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      header={
        <AdonisChatHeader
          persona={persona}
          onClose={onClose}
          onRefresh={handleOpenConfirmationModal}
        />
      }
      fullScreen
      modalClassName="rounded-t-[30px] bg-inherit"
      headerClassName="rounded-t-[30px]"
      bodyClassName="overflow-y-hidden"
      size={`h-[calc(100vh_-_env(safe-area-inset-top)_-_52px)]`}
    >
      <AdonisChatComponent
        session={session}
        chatText={chatText}
        isOpen={isOpen}
        pendingMessage={pendingMessage}
      />
      <AdonisChatConfirmationModal
        open={open}
        onCancel={handleCloseConfirmationModal}
        onConfirm={handleResetSession}
        personaName={persona.name}
      />
    </Modal>
  );
}
